import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { unauthenticated } from '@datev/ngx-dcal';
import { map, Observable } from 'rxjs';
import {
  DownloadFilter,
  DownloadType,
  MainRelease,
  MainReleaseDto,
  ServiceRelease,
  ServiceReleaseDto,
} from '../../model';
import { ProductGroupMapperService } from './product-group-mapper.service';

@Injectable({
  providedIn: 'root',
})
export class SmeService {
  private readonly apiUrl = 'api/amr/download-portal-api/v1';

  constructor(
    private readonly http: HttpClient,
    private readonly productGroupMapper: ProductGroupMapperService,
  ) {}

  getMainReleases(filter: DownloadFilter): Observable<Array<MainRelease>> {
    const params = this.getQueryParams(filter);

    return this.http
      .get<
        Array<MainReleaseDto>
      >(`${this.apiUrl}/sme/main-releases?${params.toString()}`, { context: unauthenticated() })
      .pipe(
        map(mainReleaseDtos =>
          mainReleaseDtos.map(mainReleaseDto =>
            this.mapMainReleaseDtoToMainRelease(mainReleaseDto),
          ),
        ),
      );
  }

  getServiceReleases(): Observable<Array<ServiceRelease>> {
    return this.http
      .get<
        Array<ServiceReleaseDto>
      >(`${this.apiUrl}/sme/service-releases`, { context: unauthenticated() })
      .pipe(
        map(serviceReleaseDtos =>
          serviceReleaseDtos.map(serviceReleaseDto =>
            this.mapServiceReleaseDtoToServiceRelease(serviceReleaseDto),
          ),
        ),
      );
  }

  getMainReleaseById(id: string): Observable<MainRelease> {
    return this.http
      .get<MainReleaseDto>(`${this.apiUrl}/sme/main-releases/${id}`, {
        context: unauthenticated(),
      })
      .pipe(
        map(mainReleaseDto =>
          this.mapMainReleaseDtoToMainRelease(mainReleaseDto),
        ),
      );
  }

  getServiceReleaseById(id: string): Observable<ServiceRelease> {
    return this.http
      .get<ServiceReleaseDto>(`${this.apiUrl}/sme/service-releases/${id}`, {
        context: unauthenticated(),
      })
      .pipe(
        map(serviceReleaseDto =>
          this.mapServiceReleaseDtoToServiceRelease(serviceReleaseDto),
        ),
      );
  }

  private getQueryParams(filter: DownloadFilter): HttpParams {
    let params = new HttpParams();

    if (filter.mainComponents) {
      params = params.append('main_components', filter.mainComponents);
    }

    params = params.append('is_archived', filter.isArchived);

    return params;
  }

  private mapMainReleaseDtoToMainRelease(
    mainReleaseDto: MainReleaseDto,
  ): MainRelease {
    const displayTitle = mainReleaseDto.version
      ? `${mainReleaseDto.name} ${mainReleaseDto.version}`
      : mainReleaseDto.name;
    return {
      ...mainReleaseDto,
      type: DownloadType.PAKET,
      displayTitle,
    };
  }

  private mapServiceReleaseDtoToServiceRelease(
    productDto: ServiceReleaseDto,
  ): ServiceRelease {
    const productGroupLabel = this.productGroupMapper.getProductGroupLabel(
      productDto.product_group,
    );

    const displayTitle = productDto.version
      ? `${productDto.name} ${productDto.version}`
      : productDto.name;

    return {
      ...productDto,
      productGroupLabel,
      displayTitle,
      type: DownloadType.PRODUKT,
    };
  }
}
